import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Select,
  FormControl,
  IconButton,
  Box,
  Typography,
  InputBase,
} from '@mui/material';
import { DeleteRounded } from '@mui/icons-material';
import FormSection from './ui/FormSection';
import FormRow from './ui/FormRow';
import { sanitizeNum } from '../utils/number';
import { MEAL_LABELS, MEAL_ORDER } from '../constants/mealLabels';

// Replaces AddFoodDialog + EditFoodDialogOwnAdded — the same product-name /
// kcal / mealtime / macros form, in either "add" (does its own POST) or
// "edit" (delegates to onUpdate/onDelete, matching the original contract)
// mode.
export default function FoodFormDialog({
  open,
  onClose,
  mode = 'add',
  item,
  onFoodAdded,
  onUpdate,
  onDelete,
}) {
  const isEdit = mode === 'edit';

  const [productName, setProductName] = useState('');
  const [kcal, setKcal] = useState('');
  const [meals, setMeals] = useState([]);
  const [protein, setProtein] = useState('0');
  const [fat, setFat] = useState('0');
  const [carbs, setCarbs] = useState('0');

  useEffect(() => {
    if (isEdit && item) {
      setProductName(item.product_name || '');
      setKcal(String(item.kcal_complete || ''));
      setMeals(Array.isArray(item.mealtime) ? item.mealtime : []);
      setProtein(sanitizeNum(item.protein_g ?? 0));
      setFat(sanitizeNum(item.fat_g ?? 0));
      setCarbs(sanitizeNum(item.carbs_g ?? 0));
    }
  }, [isEdit, item]);

  const resetForm = () => {
    setProductName('');
    setKcal('');
    setMeals([]);
    setProtein('0');
    setFat('0');
    setCarbs('0');
  };

  const handleAdd = () => {
    if (!productName || !kcal || meals.length === 0) {
      alert('Bitte alle Pflichtfelder ausfüllen und mindestens eine Mahlzeit auswählen.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;

    fetch('/api/food/add-own-food', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        product_name: productName,
        kcal_complete: parseInt(kcal, 10),
        mealtime: meals,
        updated_at: new Date().toISOString(),
        protein_g: Number(protein) || 0,
        fat_g: Number(fat) || 0,
        carbs_g: Number(carbs) || 0,
      }),
    })
      .then((r) => r.json())
      .then(() => {
        resetForm();
        onFoodAdded();
        onClose();
      })
      .catch((e) => console.error('Error adding food:', e));
  };

  const handleUpdate = () => {
    if (!productName || !kcal) {
      alert('Bitte alle Felder ausfüllen!');
      return;
    }
    onUpdate(item.id, productName, kcal, meals, Number(protein) || 0, Number(fat) || 0, Number(carbs) || 0);
  };

  const handleDelete = () => {
    if (window.confirm('Möchten Sie dieses Lebensmittel wirklich löschen?')) onDelete(item.id);
  };

  const isAddDisabled = !productName || !kcal || parseInt(kcal, 10) <= 0 || meals.length === 0;

  const macros = [
    { label: 'Protein', value: protein, set: setProtein },
    { label: 'Fett', value: fat, set: setFat },
    { label: 'Carbs', value: carbs, set: setCarbs },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isEdit ? 'Lebensmittel bearbeiten' : 'Neues Lebensmittel'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormSection>
            <FormRow
              label="Name"
              placeholder="Produktname"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
            />
            <FormRow
              label="Kalorien"
              unit="kcal"
              value={kcal}
              onChange={(e) => setKcal(e.target.value)}
              onFocus={(e) => e.target.select()}
              slotProps={{ inputMode: 'numeric' }}
            />
            <FormRow label="Mahlzeiten">
              <FormControl fullWidth variant="standard">
                <Select
                  value={meals}
                  onChange={(e) => setMeals(e.target.value)}
                  multiple
                  disableUnderline
                  renderValue={(s) => s.map((v) => MEAL_LABELS[v] || v).join(', ')}
                  sx={{ justifyContent: 'flex-end', textAlign: 'right' }}
                >
                  {MEAL_ORDER.map((key) => (
                    <MenuItem key={key} value={key}>
                      {MEAL_LABELS[key]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FormRow>
          </FormSection>

          <Typography variant="overline" color="text.secondary">
            Makros / Portion
          </Typography>

          <FormSection>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                '& > *:not(:last-child)': { borderRight: '1px solid', borderColor: 'divider' },
              }}
            >
              {macros.map(({ label, value, set }) => (
                <Box
                  key={label}
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, py: 1.5 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <InputBase
                    value={value}
                    onChange={(e) => set(sanitizeNum(e.target.value))}
                    onFocus={(e) => e.target.select()}
                    onBlur={() => {
                      if (value === '') set('0');
                    }}
                    inputProps={{ inputMode: 'decimal', style: { textAlign: 'center', fontWeight: 600 } }}
                    sx={{ width: '100%' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    g
                  </Typography>
                </Box>
              ))}
            </Box>
          </FormSection>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        {isEdit ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
            <IconButton onClick={handleDelete} color="error" sx={{ border: '1px solid', borderColor: 'error.main' }}>
              <DeleteRounded />
            </IconButton>
            <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
              <Button onClick={onClose} variant="outlined" sx={{ flex: 1 }}>
                Abbrechen
              </Button>
              <Button onClick={handleUpdate} variant="contained" sx={{ flex: 1, fontWeight: 700 }}>
                Speichern
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Button onClick={onClose} variant="outlined" sx={{ flex: 1 }}>
              Abbrechen
            </Button>
            <Button onClick={handleAdd} variant="contained" disabled={isAddDisabled} sx={{ flex: 1, fontWeight: 700 }}>
              Hinzufügen
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
